import { formatCurrency } from "@scandotcom/react";
import { IconCalendarTime, IconMapPin, IconScan } from "@tabler/icons-react";
import classNames from "classnames";
import React from "react";
import { IconRapid } from "../../Icons";
import { formatISOString } from "../../utils";
import type { LocationResult, ScanType } from "../types";
import { AvailabilitySlot } from "./useAvailabilitySlots";
import { useBodyParts } from "../SearchForm/useBodyParts";

interface Props {
  location: Pick<LocationResult, "id" | "name" | "next_day_surcharge">;
  modality: ScanType;
  bodyPartIds: number[];
  slot: AvailabilitySlot;
  onClose: () => void;
  onConfirm: () => void;
  hidePrices?: boolean;
}

export const ConfirmationModalContent: React.FC<Props> = ({
  location,
  bodyPartIds,
  modality,
  slot,
  onClose,
  onConfirm,
  hidePrices,
}) => {
  const detailsStyles =
    "p-4 flex text-base font-medium items-center rounded-xl";
  const infoStyles = classNames(
    detailsStyles,
    "gap-4 text-neutral-900 bg-neutral-50"
  );

  // TODO: Use real data for the extra cost
  const extraCost = slot?.next_day_slot
    ? location.next_day_surcharge || null
    : null;

  // We only have the selected body part IDs as the full list can change depending
  // on which scan type is selected. Therefore we call the API to get a list of
  // body parts for the current scan type and extract the selected ones by their ID.
  const { bodyParts: bodyPartsForScanType } = useBodyParts({
    selectedScanTypeId: modality.id,
    initialBodyParts: [],
  });
  const selectedBodyParts = bodyPartsForScanType.filter(({ id }) =>
    bodyPartIds.includes(id)
  );
  const bodyPartsValue = selectedBodyParts.map(({ name }) => name).join(", ");

  return (
    <>
      <div className="grid gap-3">
        <div className={infoStyles} data-testid="details-scan">
          <IconScan className="h-8 w-8 stroke-1" />
          <div className="space-y-2">
            <div className="text-sm font-normal text-neutral-600">
              {modality?.name} Scan
            </div>
            {bodyPartsValue}
          </div>
        </div>

        <div data-testid="details-center" className={infoStyles}>
          <IconMapPin className="h-8 w-8 stroke-1" />
          <div className="space-y-2">
            <div className="text-sm font-normal text-neutral-600">
              Scanning Center
            </div>
            {location.name}
          </div>
        </div>

        {slot && (
          <div data-testid="details-appointment" className={infoStyles}>
            <IconCalendarTime className="h-8 w-8 stroke-1" />
            <div className="space-y-2">
              <time
                className="flex items-center gap-2 text-sm font-normal text-neutral-600"
                dateTime={slot.start_time}
              >
                {formatISOString(slot.start_time, "EEEE do MMM yyyy")}

                <div className="bg-warning-50 border-warning-700 text-warning-900 rounded-md border px-2 py-0.5 text-xs font-medium">
                  Pending
                </div>
              </time>
              {formatISOString(slot.start_time, "h:mm aaa")}
            </div>
          </div>
        )}

        {extraCost && (
          <div
            data-testid="details-next-day"
            className={classNames(
              detailsStyles,
              "bg-warning-50 text-warning-900 gap-2"
            )}
          >
            <IconRapid className="h-6 w-6" />
            Next day appointment
            {!hidePrices && (
              <span className="ml-auto font-normal">
                +{formatCurrency(extraCost / 100)}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="mt-auto flex flex-col-reverse items-center gap-2 sm:flex-row sm:justify-between">
        <button
          type="button"
          className="button button-tertiary sm:max-w-min-content w-full"
          onClick={onClose}
        >
          Back
        </button>

        <button
          type="button"
          className="button button-primary sm:max-w-min-content w-full"
          onClick={onConfirm}
        >
          Continue
        </button>
      </div>
    </>
  );
};
